import { useState } from "react";
import { toast } from "sonner";
import { motion } from "framer-motion";
import { useForm } from "@formspree/react";
import api from "../../lib/api";
import { submitToFormspree } from "../../lib/formspree";
import { ArrowUpRight } from "@phosphor-icons/react";

const POSITIONS = [
    "Master / Captain", "Chief Officer", "Chief Engineer", "Second Engineer",
    "Deck Cadet", "Engine Cadet", "Marine Engineer (Shore)", "Naval Architect",
    "Operations Manager", "Charter Broker", "HSEQ Officer", "Other",
];

const initial = {
    full_name: "", email: "", phone: "", position: POSITIONS[0],
    experience_years: "", rank: "", nationality: "", cover_letter: "",
};

export default function CareersForm() {
    const [data, setData] = useState(initial);
    const [loading, setLoading] = useState(false);
    const [, , formspreeReset] = useForm("mwvzvlqa");

    const update = (k) => (e) => setData((d) => ({ ...d, [k]: e.target.value }));

    const submit = async (e) => {
        e.preventDefault();
        setLoading(true);
        try {
            const payload = {
                ...data,
                experience_years: data.experience_years ? parseInt(data.experience_years, 10) : null,
            };
            const fsPayload = {
                _subject: `Windermere · Career Application · ${data.position} · ${data.full_name}`,
                _form: "careers",
                ...payload,
            };
            const [fsRes, dbRes] = await Promise.allSettled([
                submitToFormspree(fsPayload),
                api.post("/careers", payload),
            ]);
            const ok = (fsRes.status === "fulfilled" && fsRes.value) || dbRes.status === "fulfilled";
            if (ok) {
                toast.success(dbRes?.value?.data?.message || "Application submitted. Our crewing team will review it.");
                setData(initial);
                formspreeReset?.();
            } else {
                toast.error("Could not submit application");
            }
        } finally {
            setLoading(false);
        }
    };

    return (
        <motion.form
            onSubmit={submit}
            data-testid="careers-form"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="wms-glass p-7 md:p-10"
        >
            <span className="wms-overline">Join the Crew</span>
            <h3 className="mt-3 font-display text-2xl md:text-3xl">Application Form</h3>

            <div className="mt-8 grid grid-cols-1 md:grid-cols-2 gap-px bg-white/5 border border-white/5">
                {[
                    ["full_name", "Full Name *", "text", true],
                    ["email", "Email *", "email", true],
                    ["phone", "Phone", "tel", false],
                    ["nationality", "Nationality", "text", false],
                    ["rank", "Current / Last Rank", "text", false],
                    ["experience_years", "Years of Experience", "number", false],
                ].map(([k, l, t, req]) => (
                    <label key={k} className="bg-[#040914] p-4">
                        <div className="text-[10px] tracking-[0.28em] uppercase text-white/40">{l}</div>
                        <input
                            type={t}
                            required={req}
                            name={k}
                            value={data[k]}
                            onChange={update(k)}
                            data-testid={`careers-input-${k}`}
                            className="mt-2 w-full bg-transparent border-0 text-white outline-none placeholder-white/20"
                        />
                    </label>
                ))}
                <label className="md:col-span-2 bg-[#040914] p-4">
                    <div className="text-[10px] tracking-[0.28em] uppercase text-white/40">Position Applied *</div>
                    <select
                        required
                        name="position"
                        value={data.position}
                        onChange={update("position")}
                        data-testid="careers-position"
                        className="mt-2 w-full bg-transparent border-0 text-white outline-none"
                    >
                        {POSITIONS.map((p) => (
                            <option key={p} value={p} className="bg-[#0B1324] text-white">{p}</option>
                        ))}
                    </select>
                </label>
                <label className="md:col-span-2 bg-[#040914] p-4">
                    <div className="text-[10px] tracking-[0.28em] uppercase text-white/40">Cover Letter</div>
                    <textarea
                        rows={5}
                        name="cover_letter"
                        value={data.cover_letter}
                        onChange={update("cover_letter")}
                        data-testid="careers-cover-letter"
                        className="mt-2 w-full bg-transparent border-0 text-white outline-none placeholder-white/20 resize-none"
                        placeholder="Tell us about your sea time, certifications and aspirations..."
                    />
                </label>
            </div>

            <button
                type="submit"
                disabled={loading}
                data-testid="careers-submit-btn"
                className="mt-8 inline-flex items-center gap-3 px-7 py-4 bg-white text-[#040914] text-xs uppercase tracking-[0.24em] font-medium hover:bg-[#48CAE4] transition-colors disabled:opacity-60"
            >
                {loading ? "Submitting..." : "Submit Application"}
                <ArrowUpRight size={16} weight="bold" />
            </button>
        </motion.form>
    );
}
